import { X, Filter } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { MultiSelect, type Option } from "../../ui/multi-select";
import type { FilterSelections, CumulativePerformance } from "../hooks/useFilteredPerformance";
import { Badge } from "../../ui/badge";

interface FilterSummaryBarProps {
  filters: FilterSelections;
  onFiltersChange: (filters: FilterSelections) => void;
  cumulativePerformance: CumulativePerformance | null | undefined;
  isLoading?: boolean;
}

export function FilterSummaryBar({
  filters,
  onFiltersChange,
  cumulativePerformance,
  isLoading,
}: FilterSummaryBarProps) {
  // Extract unique values from performance data
  const channelOptions: Option[] = [
    { value: "Shopify", label: "Shopify" },
    { value: "Wholesale", label: "Wholesale" },
  ];

  const categoryOptions: Option[] =
    cumulativePerformance?.category_performance
      .filter((item) => item.category && item.category.trim() !== "")
      .map((item) => ({
        value: item.category,
        label: item.category,
      }))
      .filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value)
      )
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const genderOptions: Option[] =
    cumulativePerformance?.gender_performance
      .filter((item) => item.gender && item.gender.trim() !== "")
      .map((item) => ({
        value: item.gender,
        label: item.gender,
      }))
      .filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value)
      )
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const collectionOptions: Option[] =
    cumulativePerformance?.collection_performance
      .filter((item) => item.collection && item.collection.trim() !== "")
      .map((item) => ({
        value: item.collection,
        label: item.collection,
      }))
      .filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value)
      )
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const colorOptions: Option[] =
    cumulativePerformance?.color_performance
      .filter((item) => item.color && item.color.trim() !== "")
      .map((item) => ({
        value: item.color,
        label: item.color,
      }))
      .filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value)
      )
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const styleOptions: Option[] =
    cumulativePerformance?.style_performance
      .filter((item) => item.style && item.style.trim() !== "")
      .map((item) => ({
        value: item.style,
        label: item.style,
      }))
      .filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value)
      )
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const handleClearAll = () => {
    onFiltersChange({
      channels: [],
      categories: [],
      genders: [],
      collections: [],
      colors: [],
      styles: [],
    });
  };

  const hasActiveFilters =
    filters.channels.length > 0 ||
    filters.categories.length > 0 ||
    filters.genders.length > 0 ||
    filters.collections.length > 0 ||
    filters.colors.length > 0 ||
    filters.styles.length > 0;

  const totalActiveFilters =
    filters.channels.length +
    filters.categories.length +
    filters.genders.length +
    filters.collections.length +
    filters.colors.length +
    filters.styles.length;

  if (isLoading) {
    return (
      <Card className="mb-6 border-2 border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading filters...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-2 border-primary/20 bg-primary/5">
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Quick Filter Summary</h3>
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2">
                  {totalActiveFilters} active
                </Badge>
              )}
            </div>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>

          {/* Notice */}
          <div className="text-xs text-muted-foreground bg-muted/50 border border-muted rounded-md px-3 py-2">
            <span className="font-medium">Note:</span> Filters only apply to the summary below and do not affect other charts on this page.
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Channel</label>
              <MultiSelect
                options={channelOptions}
                selected={filters.channels}
                onChange={(selected) =>
                  onFiltersChange({ ...filters, channels: selected })
                }
                placeholder="Select channels"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <MultiSelect
                options={categoryOptions}
                selected={filters.categories}
                onChange={(selected) =>
                  onFiltersChange({ ...filters, categories: selected })
                }
                placeholder="Select categories"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Gender</label>
              <MultiSelect
                options={genderOptions}
                selected={filters.genders}
                onChange={(selected) =>
                  onFiltersChange({ ...filters, genders: selected })
                }
                placeholder="Select genders"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Collection</label>
              <MultiSelect
                options={collectionOptions}
                selected={filters.collections}
                onChange={(selected) =>
                  onFiltersChange({ ...filters, collections: selected })
                }
                placeholder="Select collections"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Color</label>
              <MultiSelect
                options={colorOptions}
                selected={filters.colors}
                onChange={(selected) =>
                  onFiltersChange({ ...filters, colors: selected })
                }
                placeholder="Select colors"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Style</label>
              <MultiSelect
                options={styleOptions}
                selected={filters.styles}
                onChange={(selected) =>
                  onFiltersChange({ ...filters, styles: selected })
                }
                placeholder="Select styles"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


